import { c as _c } from "react/compiler-runtime";
import { type DragEvent, type MouseEvent, type PointerEvent, useEffect, useRef, useState } from 'react';
const DRAG_THRESHOLD = 5;
interface DragState {
  startX: number;
  startScrollLeft: number;
  isDragging: boolean;
}
export const useHorizontalDragScroll = () => {
  const $ = _c(10);
  const elementRef = useRef(null);
  const dragRef = useRef(null);
  const hasDraggedRef = useRef(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const element = elementRef.current;
      if (!element) {
        return;
      }
      const syncScrollState = () => {
        setCanScrollPrev(element.scrollLeft > 1);
        setCanScrollNext(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
      };
      syncScrollState();
      element.addEventListener("scroll", syncScrollState, {
        passive: true
      });
      const resizeObserver = new ResizeObserver(syncScrollState);
      resizeObserver.observe(element);
      return () => {
        element.removeEventListener("scroll", syncScrollState);
        resizeObserver.disconnect();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = direction => {
      const element_0 = elementRef.current;
      const firstItem = element_0?.firstElementChild;
      if (!element_0 || !(firstItem instanceof HTMLElement)) {
        return;
      }
      const style = getComputedStyle(element_0);
      const gap = parseFloat(style.columnGap) || 0;
      const itemStep = firstItem.offsetWidth + gap;
      const viewportWidth = element_0.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const visibleCount = Math.max(1, Math.floor((viewportWidth + gap) / itemStep));
      const currentIndex = Math.round(element_0.scrollLeft / itemStep);
      element_0.scrollTo({
        left: (currentIndex + direction * visibleCount) * itemStep,
        behavior: "smooth"
      });
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const scrollByPage = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = event => {
      if (event.pointerType !== "mouse" || event.button !== 0) {
        return;
      }
      hasDraggedRef.current = false;
      dragRef.current = {
        startX: event.clientX,
        startScrollLeft: event.currentTarget.scrollLeft,
        isDragging: false
      };
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handlePointerDown = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event_0 => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      const deltaX = event_0.clientX - drag.startX;
      if (!drag.isDragging && Math.abs(deltaX) > DRAG_THRESHOLD) {
        drag.isDragging = true;
        hasDraggedRef.current = true;
        event_0.currentTarget.setPointerCapture(event_0.pointerId);
      }
      if (drag.isDragging) {
        event_0.currentTarget.scrollLeft = drag.startScrollLeft - deltaX;
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handlePointerMove = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      dragRef.current = null;
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handlePointerEnd = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerEnd,
      onPointerCancel: handlePointerEnd,
      onDragStart: _temp,
      onClickCapture: event_2 => {
        if (hasDraggedRef.current) {
          event_2.preventDefault();
          event_2.stopPropagation();
          hasDraggedRef.current = false;
        }
      }
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== canScrollNext || $[8] !== canScrollPrev) {
    t7 = {
      elementRef,
      canScrollPrev,
      canScrollNext,
      scrollByPage,
      dragHandlers: t6
    };
    $[7] = canScrollNext;
    $[8] = canScrollPrev;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
};
function _temp(event_1) {
  return event_1.preventDefault();
}